import { WithAuthorization } from "../../context";
import { Page } from "../../components/Page";
import { Card } from "../../components/Card";
import {
  Button,
  Center,
  Flex,
  IconButton,
  Menu,
  MenuButton,
  MenuItem,
  MenuList, Text,
  useBreakpointValue,
  useDisclosure,
  useToast
} from "@chakra-ui/react";
import { DataTable } from "../../components/DataTable";
import { DeleteDialog } from "../DataEntryPlanning/components/DeleteDialog";
import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  calculateDataTableState,
  constructQueryParamsString, formatDate,
  getSortDirection,
  handlePageSizeChange,
  showErrorToast,
  showSuccessToast
} from "../../utils";
import {
  supplierNotesKeys,
  useCreateSupplierNotes, useDeleteSupplierNotes,
  useGetSupplierNotes, useUpdateSupplierNotes
} from "../../hooks/query";
import { hideAppSpinner, showAppSpinner } from "../../context/Store/slices/appSpinnerSlice";
import { FaBars, FaPen, FaTrash } from "react-icons/fa";
import { useDispatch } from "react-redux";
import { useQueryClient } from "react-query";
import { SupplierNotesModal } from "./components";

export const SUPPLIER_NOTES_STATUS = Object.freeze({
  "OPEN": "OPEN",
  "COMPLETED": "COMPLETED"
});

function SupplierNotes() {
  const { t } = useTranslation("translation", {
    keyPrefix: "supplierNotes"
  });
  const toast = useToast();
  const dispatch = useDispatch();
  const queryClient = useQueryClient();

  const {
    isOpen: isConfirmDialogOpen,
    onOpen: onConfirmDialogOpen,
    onClose: onConfirmDialogClose
  } = useDisclosure();
  const {
    isOpen: isNotesModalOpen,
    onOpen: onNotesModalOpen,
    onClose: onNotesModalClose
  } = useDisclosure();

  const [notesList, setNotesList] = useState([]);
  const [selectedRowId, setSelectedRowId] = useState(null);
  const [notesModalData, setNotesModalData] = useState(null);

  const [dataTableState, setDataTableState] = useState({
    queryPageIndex: 0,
    queryPageSize: 10,
    queryPageSortBy: [],
    totalElements: 0,
    totalPages: 0,
    filterObj: {}
  });

  const {
    totalPages,
    filterObj,
    queryPageIndex,
    queryPageSize,
    queryPageSortBy,
    totalElements
  } = dataTableState;

  const manageDataTableState = (newValObject) => {
    setDataTableState((prevState) => {
      return {
        ...prevState,
        ...calculateDataTableState({
          prevState,
          newValObject,
          sortAccessors: [
            { originalAccessor: "supplier", alternativeAccessor: "supplier.name" },
            { originalAccessor: "category", alternativeAccessor: "supplier.category.name" }
          ]
        })
      };
    });
  };

  const getQueryParamsString = () => {
    const queryParams = {
      page: queryPageIndex,
      size: queryPageSize,
      ...filterObj,
      sort: queryPageSortBy
    };
    return constructQueryParamsString(queryParams);
  };

  const {
    isFetching: isFetchingNotes,
    refetch: refetchNotes
  } = useGetSupplierNotes(getQueryParamsString(), {
      onSuccess: (data) => {
        const pageContent = data?.content ?? [];
        const notes = pageContent.map((el) => {
          return {
            id: el.id,
            supplier: el.supplier,
            category: el.category,
            contactPerson: el.contactPerson,
            internalNote: el.internalNote,
            supplierNote: el.supplierNote,
            status: el.status,
            createdBy: el.createdBy,
            lastModifiedDate: formatDate(el?.lastModifiedDate ?? null)
          };
        });
        setNotesList(notes);
        handlePageSizeChange(data, queryPageIndex, manageDataTableState);
      },
      onError: err => showErrorToast(toast, err.response?.data?.parameters?.message ?? err.message),
      cacheTime: 0
    }
  );

  const getRequestConfig = (successMessage) => {
    return {
      onMutate: () => dispatch(showAppSpinner()),
      onSuccess: () => {
        if (isNotesModalOpen) {
          closeNotesModal();
        }
        showSuccessToast(toast, successMessage);
        queryClient.invalidateQueries(supplierNotesKeys.supplierNotes());
      },
      onError: err => showErrorToast(toast, err.response?.data?.parameters?.message ?? err.message),
      onSettled: () => dispatch(hideAppSpinner())
    };
  };

  const { mutate: createNotes } = useCreateSupplierNotes(getRequestConfig(t("messages.notesCreated")));
  const { mutate: updateNotes } = useUpdateSupplierNotes(getRequestConfig(t("messages.notesUpdated")));

  const handleAddNewNote = () => {
    onNotesModalOpen();
  };

  const closeNotesModal = () => {
    setNotesModalData(null);
    onNotesModalClose();
  };

  const handleNotesModalSubmit = (id, data) => {
    if (typeof id !== "undefined") {
      updateNotes({ id, data });
    } else {
      createNotes(data);
    }
  };

  const showNoteText = useBreakpointValue({
    base: false,
    "2xl": true
  });

  const columns = useMemo(() => {
      function handleEditNote(data) {
        setNotesModalData(data.row.original);
        onNotesModalOpen();
      }

      function handleDelete(rowId) {
        setSelectedRowId(rowId);
        onConfirmDialogOpen();
      }

      return [
        {
          header: t("gridHeaders.supplierName"),
          accessorKey: "supplier",
          size: 150,
          sortDirection: getSortDirection(queryPageSortBy, "supplier.name")
        },
        {
          header: t("gridHeaders.category"),
          accessorKey: "category",
          size: 150,
          sortDirection: getSortDirection(queryPageSortBy, "supplier.category.name")
        },
        {
          header: t("gridHeaders.lastModifiedDate"),
          accessorKey: "lastModifiedDate",
          size: 150,
          sortDirection: getSortDirection(queryPageSortBy, "lastModifiedDate"),
          enableColumnFilter: false
        },
        {
          header: t("gridHeaders.supplierNote"),
          accessorKey: "supplierNote",
          size: 250,
          enableColumnFilter: false,
          isSortable: false,
          skipNumericCheck: true,
          cell: (el) => {
            const cell = el.row.original;

            return (
              <Flex alignItems="center" gap={2}>
                {showNoteText && <Text
                  minWidth={"130px"}
                  width={"70%"}
                  overflow="hidden"
                  textOverflow="ellipsis"
                  whiteSpace="nowrap"
                >
                  {cell.supplierNote ?? " "}
                </Text>}
                <Button
                  minWidth={"130px"}
                  width={"30%"}
                  paddingInline={4}
                  title={t("labels.clickToSee")}
                  onClick={() => {
                    handleEditNote(el);
                  }}
                >
                  {t("buttons.viewNote")}
                </Button>
              </Flex>
            );
          }
        },
        {
          header: t("gridHeaders.createdBy"),
          accessorKey: "createdBy",
          size: 150,
          sortDirection: getSortDirection(queryPageSortBy, "createdBy"),
          enableColumnFilter: false
        },
        {
          header: <div style={{ textAlign: "left" }}><b>{t("gridHeaders.actions")}</b></div>,
          accessorKey: "action",
          size: 80,
          enableColumnFilter: false,
          isSortable: false,
          cell: row => (
            <Menu isLazy>
              <MenuButton
                as={IconButton}
                aria-label="Actions"
                icon={<FaBars />}
                variant="outline"
              />
              <MenuList>
                <MenuItem
                  icon={<FaPen color="#6EA161" />}
                  onClick={() => handleEditNote(row)}
                >
                  {t("buttons.editNote")}
                </MenuItem>
                <MenuItem
                  icon={<FaTrash color="red" opacity="0.6" />}
                  onClick={() => handleDelete(row.row.original.id)}
                >
                  {t("buttons.deleteNote")}
                </MenuItem>
              </MenuList>
            </Menu>
          )
        }
      ];
    }, [t, queryPageSortBy, onNotesModalOpen, onConfirmDialogOpen, showNoteText]
  );

  const { mutate: deleteNotes } = useDeleteSupplierNotes({
    onMutate: () => dispatch(showAppSpinner()),
    onSuccess: () => {
      onConfirmDialogClose();
      setSelectedRowId(null);
      showSuccessToast(toast, t("messages.notesDeleted"));
      queryClient.invalidateQueries(supplierNotesKeys.supplierNotes());
      refetchNotes();
    },
    onError: err => showErrorToast(toast, err.response?.data?.parameters?.message ?? err.message),
    onSettled: () => {
      dispatch(hideAppSpinner());
    }
  });

  const handleNoteDelete = () => {
    if (selectedRowId !== null) {
      deleteNotes(selectedRowId);
    }
  };

  return (
    <Page>
      <Card>
        <Center marginBlockEnd="5">
          <Button
            colorScheme="customScheme"
            width="40"
            onClick={handleAddNewNote}
          >
            + {t("buttons.addNewNote")}
          </Button>
        </Center>

        {isNotesModalOpen && <SupplierNotesModal
          modalData={notesModalData}
          isOpen={isNotesModalOpen}
          onClose={closeNotesModal}
          onSubmit={handleNotesModalSubmit}
        />}

        <DataTable
          data={notesList}
          columns={columns}
          isLoading={isFetchingNotes}
          serverSidePagination={true}
          queryTotalCount={totalElements}
          queryPageCount={totalPages}
          queryPageIndex={queryPageIndex}
          setQueryPageIndex={(value) => manageDataTableState({ queryPageIndex: value })}
          queryPageSize={queryPageSize}
          setQueryPageSize={(value) => manageDataTableState({ queryPageSize: value })}
          queryPageSortBy={queryPageSortBy}
          setQueryPageSortBy={(value) => manageDataTableState({ queryPageSortBy: value })}
          filterObj={filterObj}
          setFilterObj={(newFilterObj) => manageDataTableState({
            filterObj: newFilterObj,
            queryPageIndex: 0
          })}
          enableFilters={true}
          enableSorting={true}
          containerHeight="var(--chakra-sizes-2xl)"
        />

        <DeleteDialog
          isOpen={isConfirmDialogOpen}
          onClose={onConfirmDialogClose}
          onDelete={handleNoteDelete}
          entityName={"Note"}
        />
      </Card>
    </Page>
  );
}

export default WithAuthorization(SupplierNotes);